import { useEffect } from 'react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import { Mail, Phone, MapPin, Clock, Facebook } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCmsImages, useCmsContent } from '../context/CmsImagesContext';

export default function Contact() {
  const { t } = useLanguage();
  const { getImage, getText, companyInfo: contactInfo } = useCmsContent();

  useEffect(() => {
    document.title = t({ da: "FTR Service ApS - Kontakt", en: "FTR Service ApS - Contact" });
  }, [t]);

  return (
    <Layout>
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={getImage('contact_hero', '/images/hero-cleaner.png', { width: 1400 })} 
            alt="" 
            className="w-full h-full object-cover opacity-10" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide uppercase bg-brand-primary/10 text-brand-primary mb-6">
              {getText('kontakt_hero', 'underoverskrift', t({ da: "Kontakt Os", en: "Contact Us" }))}
            </span>
            <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              {getText('kontakt_hero', 'overskrift', t({ da: "Lad os tage os af rengøringen", en: "Let us take care of the cleaning" }))}
            </h1>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
              {getText('kontakt_hero', 'tekst', t({ 
                da: "Har du spørgsmål, eller ønsker du et uforpligtende tilbud på rengøring? Vi sidder klar til at hjælpe dig med at finde den helt rigtige løsning.",
                en: "Do you have questions, or do you want a non-binding quote for cleaning? We are ready to help you find the right solution."
              }))}
            </p>
            <div className="flex gap-4">
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="bg-[#85d7f9] text-white px-10 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-xl shadow-[#85d7f9]/20">
                {getText('kontakt_hero', 'knap_tekst', t({ da: "Ring til os nu", en: "Call us now" }))}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-5 space-y-12">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-8">
                  {getText('kontakt_info_sektion', 'overskrift', t({ da: "Vores kontaktoplysninger", en: "Our contact information" }))}
                </h2>
                <div className="space-y-8">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-white text-brand-primary border border-brand-primary/10 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{t({ da: "Adresse", en: "Address" })}</h4>
                      <p className="text-gray-600 leading-relaxed">
                        {contactInfo.name}<br />
                        {contactInfo.owner}<br />
                        {contactInfo.address}<br />
                        {contactInfo.zipCity}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-white text-brand-primary border border-brand-primary/10 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">{t({ da: "Telefon", en: "Phone" })}</h4>
                      <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-sky-600 transition-colors">
                        {contactInfo.phone}
                      </a>
                      <p className="text-xs text-gray-400 mt-1">{t({ da: "Mandag — Fredag: 08:00 - 16:00", en: "Monday — Friday: 08:00 - 16:00" })}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-white text-brand-primary border border-brand-primary/10 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Email</h4>
                      <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-sky-600 transition-colors break-all">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-white text-brand-primary border border-brand-primary/10 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">
                        {getText('kontakt_info_sektion', 'underoverskrift', t({ da: "Responsgaranti", en: "Response guarantee" }))}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {getText('kontakt_info_sektion', 'tekst', t({ 
                          da: "Vi bestræber os på at besvare alle henvendelser inden for 24 timer på hverdage.",
                          en: "We strive to answer all inquiries within 24 hours on weekdays."
                        }))}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-white text-brand-primary border border-brand-primary/10 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                      <Facebook className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Facebook</h4>
                      <a 
                        href={contactInfo.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-gray-600 hover:text-[#1877F2] transition-colors"
                      >
                        {t({ da: "Følg os på Facebook", en: "Follow us on Facebook" })}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-gray-50 rounded-[2rem] border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t({ da: "Har du brug for akut hjælp?", en: "Do you need urgent help?" })}</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {t({ 
                    da: "Ved presserende opgaver eller akutte rengøringsbehov anbefaler vi, at du ringer direkte til os på telefonen.",
                    en: "For urgent tasks or acute cleaning needs, we recommend that you call us directly on the phone."
                  })}
                </p>
                <a href={`tel:${contactInfo.phone}`} className="inline-flex items-center gap-2 text-sky-600 font-bold hover:gap-3 transition-all">
                  {t({ da: "Ring nu", en: "Call now" })} <Phone className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7 bg-white p-8 sm:p-12 rounded-[2rem] border border-gray-100 shadow-xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {getText('kontakt_formular', 'overskrift', t({ da: "Send os en besked", en: "Send us a message" }))}
              </h2>
              <p className="text-gray-500 mb-10">
                {getText('kontakt_formular', 'tekst', t({ da: "Udfyld formularen herunder, og vi kontakter dig snarest muligt.", en: "Fill out the form below and we will contact you as soon as possible." }))}
              </p>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
